/**
 * A workflow's page and a run's page (D401), opened from Apps and routines › Workflows.
 *
 *   /apps-and-routines/workflows/:projectId/:workflowId     the workflow: its picture (steps and branches, read only), what
 *                                                           it needs, its versions, its runs, and Run now (choose a machine,
 *                                                           answer what it asks)
 *   /apps-and-routines/workflow-runs/:projectId/:runId      one run: its state, who or what started it, how long it took,
 *                                                           the picture in the run's states, the approvals waiting (Approve,
 *                                                           Reject or Hold, a reason for the last two), and each step's tries
 *
 * Both hold a canvas, so they take the `data` column rather than the `form` one a single record takes elsewhere. The words are
 * the contract's: workflow, step, branch, approval, approved, rejected, held, waiting for you, not needed.
 */
import { ArrowLeft, BranchesRight, CircleCheck, Clock, Display, Hand, Hourglass, Person, Play } from '@gravity-ui/icons';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ACTION_CLASS_WORDS, OUTCOME_WORDS, ROUTINE_LISTING_STATUS_WORDS, STATE_WORDS, WORKFLOW_ERRORS, type WorkflowListing, type WorkflowStepRun } from '@aioe/shared';

import { problemText, sharedCatalogueRoutes } from '../api/client.ts';
import { Button, Empty, ErrorNote, inputClass, ListBlock, ListGroup, ListRow, PageColumn, PageHeader, selectClass, Stat, StatusMark, Tick } from '../components/ui.tsx';
import { VIEW_TONES } from '../components/ui/Art.tsx';
import { FormField, FormSheet } from '../components/ui/FormSheet.tsx';
import { WorkflowPicture } from '../components/workflows/WorkflowPicture.tsx';
import { STATE_MARK, STEP_KIND_SHORT, workflowPicture } from '../components/workflows/workflowPicture.ts';
import { durationWords, runStateMark, runStateWords, startedWords, workflowWhat } from '../components/workflows/workflowWords.ts';
import { timeAgo, useLoad } from '../hooks.ts';

const TONE = VIEW_TONES['apps-and-routines']!;
const BACK = '/apps-and-routines?show=workflows';
const runHref = (projectId: string, runId: string) => `/apps-and-routines/workflow-runs/${encodeURIComponent(projectId)}/${encodeURIComponent(runId)}`;
const STATUS_MARK = { listed: 'connected', pending: 'medium', rejected: 'disabled', withdrawn: 'offline' } as const;

function Back() {
  const navigate = useNavigate();
  return (
    <Button size="sm" variant="ghost" onPress={() => navigate(BACK)}>
      <ArrowLeft className="size-4" />
      All workflows
    </Button>
  );
}

const newerFirst = (a: WorkflowListing, b: WorkflowListing) => {
  const x = a.version.split('.').map(Number);
  const y = b.version.split('.').map(Number);
  for (let i = 0; i < 3; i++) if ((x[i] ?? 0) !== (y[i] ?? 0)) return (y[i] ?? 0) - (x[i] ?? 0);
  return 0;
};

// ── A workflow ─────────────────────────────────────────────────────────

export function WorkflowPage() {
  const { projectId = '', workflowId = '' } = useParams();
  const versions = useLoad(() => sharedCatalogueRoutes.workflowVersions(projectId, workflowId), [projectId, workflowId]);
  const runs = useLoad(() => sharedCatalogueRoutes.workflowRuns({ projectId, workflowId, days: 90 }), [projectId, workflowId], 30_000);
  const [starting, setStarting] = useState(false);
  const list = [...(versions.data?.versions ?? [])].sort(newerFirst);
  const shown = list.find((l) => l.status === 'listed') ?? list[0];
  const m = shown?.manifest;
  const added = list.reduce((n, l) => n + l.installs, 0);
  const runList = runs.data?.runs ?? [];
  const waiting = runList.filter((r) => r.run.state === 'running' && r.waitingFor.length).length;
  const listed = list.filter((l) => l.status === 'listed');

  if (versions.error) {
    return (
      <PageColumn width="data">
        <PageHeader tone={TONE} icon={<BranchesRight />} title="Workflow" actions={<Back />} />
        <ErrorNote error={versions.error} />
      </PageColumn>
    );
  }
  return (
    <PageColumn width="data">
      <PageHeader
        tone={TONE}
        icon={<BranchesRight />}
        title={m?.name ?? 'Workflow'}
        {...(m ? { subtitle: m.purpose } : {})}
        actions={
          <>
            <Back />
            {listed.length ? (
              <Button size="sm" variant="primary" onPress={() => setStarting(true)}>
                <Play className="size-4" />
                Run now
              </Button>
            ) : null}
          </>
        }
      />
      <div className="art-stats">
        <Stat tone={TONE} icon={<BranchesRight />} label="Version" value={shown?.version} detail={shown ? ROUTINE_LISTING_STATUS_WORDS[shown.status] : ''} />
        <Stat tone={TONE} icon={<Hand />} label="Steps" value={m ? m.steps.agent + m.steps.code + m.steps.approval : undefined} detail={m ? `${m.steps.approval === 1 ? '1 approval' : `${m.steps.approval} approvals`} · ${ACTION_CLASS_WORDS[m.highest].toLowerCase()}` : ''} />
        <Stat tone={TONE} icon={<Person />} label="Added" value={versions.data ? added : undefined} detail="By people, every version" />
        <Stat tone={TONE} icon={<Clock />} label="Runs, last 90 days" value={runs.data ? runList.length : undefined} detail={waiting ? `${waiting} waiting for a person` : runList[0] ? `Last ${timeAgo(runList[0].run.createdAt)}` : 'Not run yet'} needsYou={waiting > 0} />
      </div>
      {m ? (
        <ListGroup title="Steps and branches" description={`${workflowWhat(m)}. Each step runs once everything before it is done; a branch out of an approval follows its decision.`}>
          <ListBlock flush>
            <WorkflowPicture definition={m.definition} />
          </ListBlock>
        </ListGroup>
      ) : null}
      {m && (m.definition.inputs.length || m.definition.parameters?.length || m.secrets.length) ? (
        <ListGroup title="What it needs" description="Asked when it starts, asked when someone adds it, and kept keys it uses by name.">
          {m.definition.inputs.map((i) => (
            <ListRow key={`in-${i.name}`} title={i.label} description={`Asked when it starts · ${i.type === 'boolean' ? 'yes or no' : i.type}`} value={i.required ? 'Needed' : 'Optional'} />
          ))}
          {(m.definition.parameters ?? []).map((p) => (
            <ListRow key={`p-${p.name}`} title={p.label} description={`Asked when someone adds it · a ${p.kind === 'member' ? 'team member' : p.kind}`} value={p.required ? 'Needed' : 'Optional'} />
          ))}
          {m.secrets.map((s) => (
            <ListRow key={`s-${s}`} title={s} description="A kept key from Secrets, sent by name, never written out" />
          ))}
        </ListGroup>
      ) : null}
      <ListGroup title="Runs" count={runs.data ? runList.length : undefined} description="The last 90 days, newest first." empty={runs.data && !runList.length ? <Empty tone={TONE} icon={<Clock />} title="Not run yet" body="Its runs are listed here as the machines that run them report each step." compact /> : undefined}>
        <ErrorNote error={runs.error} />
        {runList.slice(0, 30).map((r) => (
          <ListRow
            key={r.run.id}
            icon={<Clock />}
            title={startedWords(r.run)}
            to={runHref(r.projectId, r.run.id)}
            description={<span className="tabular-nums">{[timeAgo(r.run.createdAt), r.deviceName ? `on ${r.deviceName}` : '', `version ${r.run.version}`, durationWords(r.durationMs, r.run.state)].filter(Boolean).join(' · ')}</span>}
            status={<StatusMark state={runStateMark(r)} label={runStateWords(r)} />}
          />
        ))}
      </ListGroup>
      <ListGroup title="Versions" count={list.length || undefined} description="A version is never changed once published; a newer one goes through the same review.">
        {list.map((l) => (
          <ListRow
            key={l.version}
            title={`Version ${l.version}`}
            description={[`Published ${timeAgo(l.publishedAt)} by ${l.publishedBy.name}`, l.decidedBy ? `${l.status === 'rejected' ? 'not accepted' : 'approved'} by ${l.decidedBy.name}` : '', l.reason ? `“${l.reason}”` : '', l.installs === 1 ? '1 person added it' : `${l.installs} people added it`].filter(Boolean).join(' · ')}
            status={<StatusMark state={STATUS_MARK[l.status]} label={ROUTINE_LISTING_STATUS_WORDS[l.status]} />}
          />
        ))}
      </ListGroup>
      {starting && shown ? <RunNow projectId={projectId} versions={listed} onClose={() => setStarting(false)} /> : null}
    </PageColumn>
  );
}

/** Run now: which version, which machine (the people who added it, the project's nodes), and what it asks for. */
function RunNow({ projectId, versions, onClose }: { projectId: string; versions: WorkflowListing[]; onClose: () => void }) {
  const navigate = useNavigate();
  const [version, setVersion] = useState(versions[0]!.version);
  const l = versions.find((v) => v.version === version) ?? versions[0]!;
  const machines = useLoad(() => sharedCatalogueRoutes.workflowMachines(projectId, l.workflowId), [projectId, l.workflowId]);
  const [deviceId, setDeviceId] = useState<string>();
  const [inputs, setInputs] = useState<Record<string, unknown>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const options = machines.data?.machines ?? [];
  const chosen = deviceId ?? options.find((o) => o.online)?.deviceId ?? options[0]?.deviceId;
  const asks = l.manifest.definition.inputs;
  const missing = asks.some((i) => i.required && i.type !== 'boolean' && (inputs[i.name] === undefined || inputs[i.name] === ''));
  const start = async () => {
    if (!chosen) return;
    setBusy(true);
    setError(undefined);
    try {
      const body: Record<string, unknown> = {};
      for (const i of asks) {
        const v = inputs[i.name];
        if (v === undefined || v === '') continue;
        body[i.name] = i.type === 'number' ? Number(v) : i.type === 'json' ? JSON.parse(String(v)) : v;
      }
      const out = await sharedCatalogueRoutes.startWorkflow(projectId, l.workflowId, { deviceId: chosen, version, inputs: body });
      navigate(runHref(projectId, out.runId));
    } catch (e) {
      setError(e instanceof SyntaxError ? 'An answer that should be JSON does not read as JSON.' : problemText(e));
      setBusy(false);
    }
  };
  return (
    <FormSheet
      title={`Run ${l.manifest.name}`}
      description="The machine you choose starts it at its next check-in and reports each step here. Approvals it reaches wait in the Inbox."
      onClose={onClose}
      footer={
        <>
          <Button size="sm" variant="ghost" onPress={onClose}>
            Cancel
          </Button>
          <Button size="sm" variant="primary" isPending={busy} isDisabled={!chosen || missing} onPress={() => void start()}>
            Run now
          </Button>
        </>
      }
    >
      {versions.length > 1 ? (
        <FormField label="Version">
          <select className={selectClass} value={version} onChange={(e) => setVersion(e.target.value)}>
            {versions.map((v) => (
              <option key={v.version} value={v.version}>
                {v.version}
              </option>
            ))}
          </select>
        </FormField>
      ) : null}
      <FormField label="Run it on" hint={options.length ? 'The desktops of the people who added or published it, and the project’s nodes.' : undefined}>
        {machines.data && !options.length ? (
          <p className="text-sm text-muted">Nobody has added this workflow on a machine yet, and no node works for this project.</p>
        ) : (
          <select className={selectClass} value={chosen ?? ''} onChange={(e) => setDeviceId(e.target.value)} disabled={!options.length}>
            {options.map((o) => (
              <option key={o.deviceId} value={o.deviceId}>
                {o.name} · {o.kind === 'node' ? 'a node' : `${o.person ?? 'someone'}’s desktop`}
                {o.online ? '' : ' · offline'}
              </option>
            ))}
          </select>
        )}
      </FormField>
      {asks.map((i) =>
        i.type === 'boolean' ? (
          <Tick key={i.name} checked={inputs[i.name] === true} onChange={(v) => setInputs({ ...inputs, [i.name]: v })} label={i.label} />
        ) : (
          <FormField key={i.name} label={`${i.label}${i.required ? '' : ' (optional)'}`} {...(i.type === 'json' ? { hint: 'As JSON.' } : {})}>
            {i.type === 'json' ? (
              <textarea className={inputClass} rows={3} value={String(inputs[i.name] ?? '')} onChange={(e) => setInputs({ ...inputs, [i.name]: e.target.value })} />
            ) : (
              <input className={inputClass} type={i.type === 'number' ? 'number' : 'text'} value={String(inputs[i.name] ?? '')} onChange={(e) => setInputs({ ...inputs, [i.name]: e.target.value })} />
            )}
          </FormField>
        ),
      )}
      <ErrorNote error={error} />
    </FormSheet>
  );
}

// ── A run ──────────────────────────────────────────────────────────────

export function WorkflowRunPage() {
  const { projectId = '', runId = '' } = useParams();
  const detail = useLoad(() => sharedCatalogueRoutes.workflowRun(projectId, runId), [projectId, runId], 10_000);
  const [selected, setSelected] = useState<string>();
  const d = detail.data;
  const run = d?.view.run;
  const picture = useMemo(() => (d?.definition && run ? workflowPicture(d.definition, { run, steps: d.steps }) : undefined), [d, run]);
  const names = new Map((d?.definition?.steps ?? []).map((s) => [s.id, s.name]));
  // A step that runs per element has a record per element under its own: they show inside the step's try, in order.
  const tries = [...(d?.steps ?? [])].filter((s) => !s.item && (!selected || s.stepId === selected)).sort((a, b) => a.queuedAt.localeCompare(b.queuedAt) || a.attempt - b.attempt);
  const items = new Map<string, WorkflowStepRun[]>();
  for (const s of d?.steps ?? []) if (s.item) items.set(`${s.stepId}:${s.attempt}`, [...(items.get(`${s.stepId}:${s.attempt}`) ?? []), s]);

  if (detail.error || !d || !run) {
    return (
      <PageColumn width="data">
        <PageHeader tone={TONE} icon={<BranchesRight />} title="Run" actions={<Back />} />
        <ErrorNote error={detail.error} />
        {!detail.error ? <p className="text-sm text-muted">Reading the run…</p> : null}
      </PageColumn>
    );
  }
  const v = d.view;
  return (
    <PageColumn width="data">
      <PageHeader tone={TONE} icon={<BranchesRight />} title={v.workflowName} subtitle={`${startedWords(run)} ${timeAgo(run.createdAt)}, in ${v.projectName}. Version ${run.version}.`} actions={<Back />} />
      <div className="art-stats">
        <Stat tone={TONE} icon={<CircleCheck />} label="State" value={run.state === 'running' && v.waitingFor.length ? 'Waiting' : runStateWords(v)} detail={run.note ?? (v.waitingFor.length ? `For ${v.waitingFor.map((w) => w.name).join(', ')}` : run.endedAt ? `Ended ${timeAgo(run.endedAt)}` : run.startedAt ? `Started ${timeAgo(run.startedAt)}` : 'Not started yet')} needsYou={run.state === 'running' && v.waitingFor.length > 0} />
        <Stat tone={TONE} icon={<Person />} label="Accountable" value={run.owner.name} detail={run.trigger.by && run.trigger.by.id !== run.owner.id ? `Started by ${run.trigger.by.name}` : 'In every audit row of the run'} />
        <Stat tone={TONE} icon={<Clock />} label="Time" value={v.durationMs !== undefined ? durationWords(v.durationMs, run.state).replace(/^(Took|Running for) /, '') : undefined} detail={run.state === 'running' || run.state === 'queued' ? 'So far' : 'From start to end'} />
        <Stat tone={TONE} icon={<Display />} label="Runs on" value={v.deviceName ?? (run.where === 'node' ? 'A node' : 'A desktop')} detail={run.where === 'repository' ? "In the repository's workbench" : run.where === 'node' ? 'A node of the organisation' : 'A desktop'} />
      </div>
      <Approvals projectId={projectId} runId={runId} decisions={d.decisions} names={names} briefs={new Map((d.definition?.steps ?? []).flatMap((s) => (s.kind === 'approval' && s.brief && !s.brief.includes('{{') ? [[s.id, s.brief] as const] : [])))} onDecided={detail.reload} />
      {d.definition && picture ? (
        <ListGroup title="Steps and branches" description="Each step in the state its newest try reports. The branches taken are drawn bold; the ones not taken are faint. Press a step to see only its tries below.">
          <ListBlock flush>
            <WorkflowPicture definition={d.definition} run={{ run, steps: d.steps }} {...(selected ? { selected } : {})} onSelect={setSelected} />
          </ListBlock>
        </ListGroup>
      ) : (
        <ListGroup title="Steps">
          <ListBlock className="text-sm text-muted">The organisation does not list the version this run runs, so its picture is not here; its steps are below as the machine reported them.</ListBlock>
        </ListGroup>
      )}
      <ListGroup
        title={selected ? `Tries of ${names.get(selected) ?? selected}` : 'Each step'}
        count={tries.length}
        description="In the order they were queued. A retry is a try of its own."
        actions={
          selected ? (
            <Button size="sm" variant="ghost" onPress={() => setSelected(undefined)}>
              Show every step
            </Button>
          ) : undefined
        }
        empty={!tries.length ? <Empty tone={TONE} icon={<Clock />} title="No step has started yet" body="The machine reports each step as it starts and ends." compact /> : undefined}
      >
        {tries.map((s) => (
          <StepTry key={s.id} s={s} name={names.get(s.stepId) ?? s.stepId} items={items.get(`${s.stepId}:${s.attempt}`) ?? []} projectId={projectId} />
        ))}
      </ListGroup>
      {Object.keys(run.inputs).length ? (
        <ListGroup title="Started with">
          {Object.entries(run.inputs).map(([k, val]) => (
            <ListRow key={k} title={d.definition?.inputs.find((i) => i.name === k)?.label ?? k} value={typeof val === 'string' ? val : JSON.stringify(val)} />
          ))}
        </ListGroup>
      ) : null}
    </PageColumn>
  );
}

/** One try of one step: its state, how long, who worked it, its answer or error, and an approval's decision and reason. */
function StepTry({ s, name, items, projectId }: { s: WorkflowStepRun; name: string; items: WorkflowStepRun[]; projectId: string }) {
  const lines: string[] = [];
  lines.push([STEP_KIND_SHORT[s.kind], s.attempt > 1 ? `try ${s.attempt}` : '', s.startedAt ? `started ${timeAgo(s.startedAt)}` : `queued ${timeAgo(s.queuedAt)}`, s.durationMs !== undefined ? durationWords(s.durationMs, s.state) : ''].filter(Boolean).join(' · '));
  if (s.place?.repository) lines.push(`In ${s.place.repository}`);
  if (s.agent) lines.push(`Worked by ${s.agent.memberId.replace(/^specialist:/, 'the specialist ').replace(/^agent:([^/]+)\//, '$1’s ')}`);
  if (s.approval?.decidedBy) lines.push(`${s.outcome ? OUTCOME_WORDS[s.outcome] : 'Decided'} by ${s.approval.decidedBy.name}${s.approval.decidedAt ? ` ${timeAgo(s.approval.decidedAt)}` : ''}${s.approval.reason ? `: “${s.approval.reason}”` : ''}`);
  if (s.approval?.timedOut) lines.push(`No one decided in time, so it was ${s.outcome === 'rejected' ? 'rejected' : 'held'}.`);
  if (s.child) lines.push('Started another workflow; open this row for its run.');
  if (s.carriedFrom) lines.push('Carried over from an earlier run.');
  if (s.error) lines.push(s.error);
  if (items.length) {
    const sorted = [...items].sort((a, b) => a.item!.index - b.item!.index);
    const done = sorted.filter((i) => i.state === 'succeeded').length;
    const failed = sorted.filter((i) => i.state === 'failed');
    lines.push(`Ran once for each of ${sorted[0]!.item!.of} ${sorted[0]!.item!.of === 1 ? 'element' : 'elements'}: ${done} done${failed.length ? `, ${failed.length} failed` : ''}${sorted.length - done - failed.length ? `, ${sorted.length - done - failed.length} still going` : ''}.`);
    for (const f of failed.slice(0, 3)) lines.push(`Element ${f.item!.index + 1}: ${f.error ?? STATE_WORDS[f.state]}`);
  }
  if (s.output !== undefined && s.state === 'succeeded' && s.kind !== 'approval') {
    const text = typeof s.output === 'string' ? s.output : JSON.stringify(s.output);
    lines.push(`Answer: ${text.length > 180 ? `${text.slice(0, 180)}…` : text}`);
  }
  const word = s.kind === 'approval' && s.state === 'succeeded' && s.outcome ? OUTCOME_WORDS[s.outcome] : STATE_WORDS[s.state];
  const mark = s.kind === 'approval' && s.outcome === 'rejected' ? 'error' : s.kind === 'approval' && s.outcome === 'held' ? 'medium' : STATE_MARK[s.state];
  return (
    <ListRow
      icon={s.kind === 'approval' ? <Hand /> : s.kind === 'agent' ? <Person /> : s.kind === 'wait' ? <Hourglass /> : <BranchesRight />}
      title={name}
      description={
        <>
          {lines.map((l, i) => (
            <span key={i} className="block">
              {l}
            </span>
          ))}
        </>
      }
      status={<StatusMark state={mark} label={word} />}
      {...(s.child ? { to: `/apps-and-routines/workflow-runs/${encodeURIComponent(projectId)}/${encodeURIComponent(s.child.runId)}` } : {})}
    />
  );
}

/** The approvals this run waits on: Approve, Reject or Hold, the last two with the reason the run records. */
function Approvals({ projectId, runId, decisions, names, briefs, onDecided }: { projectId: string; runId: string; decisions: Array<{ stepId: string; attempt: number; may: boolean; why?: string; highImpact: boolean }>; names: Map<string, string>; briefs: Map<string, string>; onDecided: () => void }) {
  const [asking, setAsking] = useState<{ stepId: string; decision: 'rejected' | 'held' }>();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState<string>();
  const [error, setError] = useState<string>();
  const [done, setDone] = useState<string>();
  if (!decisions.length) return null;
  const decide = async (stepId: string, attempt: number, decision: 'approved' | 'rejected' | 'held') => {
    setBusy(`${stepId}:${decision}`);
    setError(undefined);
    try {
      const out = await sharedCatalogueRoutes.decideWorkflowApproval(projectId, runId, { stepId, attempt, decision, ...(decision !== 'approved' ? { reason: reason.trim() } : {}) });
      setDone(`${names.get(stepId) ?? stepId}: ${out.message.replace(/\.$/, '')}. The machine records it and carries on.`);
      setAsking(undefined);
      setReason('');
      onDecided();
    } catch (e) {
      const text = problemText(e);
      setError(/sign_in_again/.test(text) ? WORKFLOW_ERRORS.sign_in_again : text);
    } finally {
      setBusy(undefined);
    }
  };
  return (
    <ListGroup title="Waiting for you" count={decisions.length} description="A person decides each approval. Rejecting or holding one asks why; the run records the reason.">
      {decisions.map((a) => {
        const name = names.get(a.stepId) ?? a.stepId;
        const open = asking?.stepId === a.stepId;
        return (
          <ListRow
            key={`${a.stepId}:${a.attempt}`}
            icon={<Hand />}
            title={name}
            description={
              <>
                {briefs.get(a.stepId) ? <span className="block">{briefs.get(a.stepId)}</span> : null}
                <span className="block">{a.may ? (a.highImpact ? 'High impact: it needs a recent sign-in.' : 'You may decide this.') : a.why}</span>
              </>
            }
            control={
              a.may && !open ? (
                <>
                  <Button size="sm" variant="primary" isPending={busy === `${a.stepId}:approved`} isDisabled={Boolean(busy)} onPress={() => void decide(a.stepId, a.attempt, 'approved')}>
                    Approve
                  </Button>
                  <Button size="sm" variant="secondary" isDisabled={Boolean(busy)} onPress={() => setAsking({ stepId: a.stepId, decision: 'held' })}>
                    Hold
                  </Button>
                  <Button size="sm" variant="danger-soft" isDisabled={Boolean(busy)} onPress={() => setAsking({ stepId: a.stepId, decision: 'rejected' })}>
                    Reject
                  </Button>
                </>
              ) : undefined
            }
          >
            {open && asking ? (
              <div className="grid gap-2 @[35rem]:flex @[35rem]:items-end">
                <label className="grid flex-1 gap-1 text-xs text-muted">
                  {asking.decision === 'rejected' ? 'Why it is rejected' : 'Why it is held'} (the run records this)
                  <input className={inputClass} value={reason} maxLength={1000} onChange={(e) => setReason(e.target.value)} autoFocus />
                </label>
                <div className="flex gap-2">
                  <Button size="sm" variant="ghost" onPress={() => setAsking(undefined)}>
                    Cancel
                  </Button>
                  <Button size="sm" variant={asking.decision === 'rejected' ? 'danger' : 'primary'} isDisabled={!reason.trim()} isPending={Boolean(busy)} onPress={() => void decide(a.stepId, a.attempt, asking.decision)}>
                    {asking.decision === 'rejected' ? 'Reject' : 'Hold'}
                  </Button>
                </div>
              </div>
            ) : null}
          </ListRow>
        );
      })}
      {error || done ? (
        <ListBlock>
          <ErrorNote error={error} />
          {done && !error ? <p className="text-sm text-muted">{done}</p> : null}
        </ListBlock>
      ) : null}
    </ListGroup>
  );
}
